import { FC } from 'react';
import { useForm, Controller } from 'react-hook-form';
import clsx from 'clsx';
import { Button, Types } from '@marriott/mi-ui-library';
import { SearchFormExpandedMobileProps } from './SearchFormExpandedMobile.types';
import { StyledSearchFormExpandedMobile, StyledExpandedForm, StyledFooter } from './SearchFormExpandedMobile.styles';
import { SearchFormFieldNew } from '../SearchFormMobile.types';
import { SearchFormData } from '../../SearchForm.types';
import { useSearchFormStore } from '../../../../store';
import { TextFormField } from '../../../../molecules';
import { getDatesBadgeLabel, getDaysBadgeLabel, getFormattedDateObject, isToday } from '../../../../utils';

export const SearchFormExpandedMobile: FC<SearchFormExpandedMobileProps> = ({ labels, onSubmit }) => {
  const { destination, dates, guestRooms, attendees, guestRoomsAndEventSpace, ctas, isSearchResultPage } = labels;

  const { setCurrentStep, persistedFormData, loader, setLoader } = useSearchFormStore();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SearchFormData>({
    defaultValues: {
      destination: persistedFormData.destination || { description: '', placeId: '' },
      dates: persistedFormData.dates || '',
      startDate: persistedFormData.startDate || '',
      endDate: persistedFormData.endDate || '',
      guestRooms: persistedFormData.guestRooms || '',
      attendees: persistedFormData.attendees || '',
      roomsAndEventSpace: {
        rooms: +persistedFormData.guestRooms || 0,
        eventAttendees: +persistedFormData.attendees || 0,
      },
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const rooms = Number(persistedFormData.guestRooms) || 0;
  const eventAttendees = Number(persistedFormData.attendees) || 0;

  const guestRoomsField = {
    displayText: [
      rooms && `${rooms} ${rooms === 1 ? guestRooms.room : guestRooms.rooms}`,
      eventAttendees && `${eventAttendees} ${eventAttendees === 1 ? attendees.attendee : attendees.attendees}`,
    ]
      .filter(Boolean)
      .join(', '),
    label:
      rooms && eventAttendees ? guestRoomsAndEventSpace.label : eventAttendees ? attendees.label : guestRooms.label,
    ariaLabel:
      rooms && eventAttendees
        ? guestRoomsAndEventSpace.ariaLabel
        : eventAttendees
        ? attendees.ariaLabel
        : guestRooms.ariaLabel,
  };

  const formattedStart = persistedFormData.startDate
    ? getFormattedDateObject(persistedFormData.startDate, 'dateWithDayMonthAndYear')
    : null;
  const formattedEnd = persistedFormData.endDate
    ? getFormattedDateObject(persistedFormData.endDate, 'dateWithDayMonthAndYear')
    : null;

  const datesBadgeLabel =
    !rooms && eventAttendees > 0
      ? getDaysBadgeLabel(formattedStart, formattedEnd, dates.days)
      : getDatesBadgeLabel(formattedStart, formattedEnd, dates.night, dates.nights);

  const submitCallback = (data: SearchFormData) => {
    setLoader(true);
    onSubmit({ ...data, ...persistedFormData } as SearchFormData);
  };

  const destinationField = (
    <Controller
      name="destination"
      control={control}
      rules={{
        required: destination.requiredError,
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={destination.label}
          ariaLabel={destination.ariaLabel}
          className="m-input-text-field"
          value={persistedFormData.destination.description}
          getInputProps={() => ({
            readOnly: true,
          })}
          showErrorMessage={!!errors.destination?.message}
          errorMessage={errors.destination?.message}
          onClick={() => setCurrentStep(SearchFormFieldNew.DESTINATION)}
        />
      )}
    />
  );

  const datesField = (
    <Controller
      name="dates"
      control={control}
      rules={{
        validate: () => {
          if (!persistedFormData.startDate) return dates.requiredError;
          if (isToday(persistedFormData.startDate)) return dates.currentDateError;
          return true;
        },
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={dates.label}
          placeholder={dates.placeholder}
          ariaLabel={dates.ariaLabel}
          className="m-input-text-field"
          value={persistedFormData.dates || ''}
          showBadgeContent={!!datesBadgeLabel}
          badgeContent={datesBadgeLabel}
          getInputProps={() => ({
            readOnly: true,
          })}
          showErrorMessage={!!errors.dates?.message}
          errorMessage={errors.dates?.message}
          onClick={() => setCurrentStep(SearchFormFieldNew.DATES)}
        />
      )}
    />
  );

  const roomsAndEventSpaceField = (
    <Controller
      name="roomsAndEventSpace"
      control={control}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={guestRoomsField.label}
          ariaLabel={guestRoomsField.ariaLabel}
          className="m-input-text-field"
          value={guestRoomsField.displayText}
          getInputProps={() => ({
            readOnly: true,
          })}
          onClick={() => setCurrentStep(SearchFormFieldNew.ROOMS_AND_EVENTSPACE)}
        />
      )}
    />
  );

  return (
    <StyledSearchFormExpandedMobile>
      <StyledExpandedForm id="search-expanded-form" onSubmit={handleSubmit(submitCallback)}>
        <div className="field-row">{destinationField}</div>
        <div className="field-row">{datesField}</div>
        <div className="field-row">{roomsAndEventSpaceField}</div>
        <StyledFooter>
          <Button
            type={Types.ButtonTypeVariation.Submit}
            ariaLabel={ctas.findAriaLabel}
            className={clsx('m-button-primary', 'w-100', loader && 'm-button skeleton-loader')}
            isDisabled={loader}
          >
            <span className="icon-search" /> {isSearchResultPage ? ctas.update : ctas.find}
          </Button>
        </StyledFooter>
      </StyledExpandedForm>
    </StyledSearchFormExpandedMobile>
  );
};
